import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { ChevronDown, ChevronUp, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { fmtDayLabel } from "../lib/time";
import { Label, InfoBadge } from "./common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const dayLabel = fmtDayLabel;

export default function RatingsCollected7d({ initialLocation = "all", testId = "ratings-7d" }) {
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState(initialLocation || "all");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeDay, setActiveDay] = useState(null);

  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { setLocId(initialLocation || "all"); }, [initialLocation]);
  useEffect(() => {
    setD(null);
    const q = locId !== "all" ? `?location_id=${locId}` : "";
    api.get(`/analytics/ratings-7d${q}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [locId]);

  const chart = (d?.series || []).map((s) => ({ ...s, label: dayLabel(s.date), trusted: s.total - s.flagged }));
  const onBar = (p) => { const date = p?.payload?.date || p?.date; if (date) { setActiveDay(date); setOpen(true); } };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-1.5"><Label>Ratings Collected · Last 7 days</Label>
          <InfoBadge title="Ratings Collected" testId={`${testId}-info`}
            text="How many customer ratings were captured each day over the last 7 days. Flagged ratings (held for trust review) are shown separately. Click a day or open the breakdown to see counts per employee." /></span>
        <div className="flex items-center gap-4">
          {d && !d.error && (
            <div className="flex items-center gap-3 text-sm">
              <span data-testid={`${testId}-total`}><span className="sv-metric text-xl font-bold text-slate-900">{d.total}</span> <span className="text-slate-400">total</span></span>
              <span className="flex items-center gap-1 text-amber-600" data-testid={`${testId}-flagged`}><ShieldAlert className="h-4 w-4" /> {d.flagged} flagged</span>
            </div>
          )}
          <Select value={locId} onValueChange={(v) => { setLocId(v); setActiveDay(null); }}>
            <SelectTrigger className="w-48" data-testid={`${testId}-location-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid={`${testId}-loc-all`}>All locations</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`${testId}-loc-${l.id}`}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-3 [&_*:focus]:outline-none" style={{ width: "100%", height: 240 }} data-testid={`${testId}-chart`}>
        {!d ? <p className="py-20 text-center text-sm text-slate-400">Loading…</p> : d.error ? <p className="py-20 text-center text-sm text-slate-400">Could not load ratings.</p> : (
          <ResponsiveContainer>
            <BarChart data={chart} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} width={40} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="trusted" name="Trusted" stackId="a" fill="#4338CA" onClick={onBar} cursor="pointer" />
              <Bar dataKey="flagged" name="Flagged" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} onClick={onBar} cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {d && !d.error && (
        <>
          <button onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle-table`}
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} {open ? "Hide" : "Show"} employee breakdown
          </button>
          {open && <EmployeeTable d={d} activeDay={activeDay} testId={testId} />}
        </>
      )}
    </div>
  );
}

function EmployeeTable({ d, activeDay, testId }) {
  const hl = (date) => (date === activeDay ? "bg-indigo-50" : "");
  return (
    <div className="mt-3 overflow-x-auto sv-scroll">
      <table className="w-full min-w-[720px] text-sm" data-testid={`${testId}-table`}>
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2">Employee</th>
            {d.dates.map((x) => <th key={x} className={`text-center ${hl(x)}`}>{dayLabel(x)}</th>)}
            <th className="text-center">Total</th><th className="text-center">Flagged</th>
          </tr>
        </thead>
        <tbody>
          {d.employees.length === 0 && <tr><td colSpan={d.dates.length + 3} className="py-6 text-center text-slate-400">No ratings in the last 7 days.</td></tr>}
          {d.employees.map((r) => (
            <tr key={r.employee_id || "unassigned"} className="border-b border-slate-100" data-testid={`${testId}-row-${r.employee_id || "unassigned"}`}>
              <td className="py-2 font-semibold text-slate-800">{r.name}{r.job_title && <span className="block text-xs font-normal text-slate-400">{r.job_title}</span>}</td>
              {d.dates.map((x) => <td key={x} className={`text-center ${r.counts[x] ? "text-slate-700" : "text-slate-300"} ${hl(x)}`}>{r.counts[x] || 0}</td>)}
              <td className="text-center font-bold text-slate-900">{r.total}</td>
              <td className="text-center text-amber-600">{r.flagged}</td>
            </tr>
          ))}
        </tbody>
        {d.employees.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-slate-200 font-bold text-slate-900" data-testid={`${testId}-totals-row`}>
              <td className="py-2">Total</td>
              {d.series.map((s) => <td key={s.date} className={`text-center ${hl(s.date)}`}>{s.total}</td>)}
              <td className="text-center">{d.total}</td><td className="text-center text-amber-600">{d.flagged}</td>
            </tr>
          </tfoot>
        )}
      </table>
      {d.former_total > 0 && (
        <p className="mt-2 text-xs text-slate-400" data-testid={`${testId}-former-note`}>
          Totals include {d.former_total} rating{d.former_total === 1 ? "" : "s"} for former or unassigned employees, who are not listed.
        </p>
      )}
    </div>
  );
}
